import React from "react";
import {
  AbsoluteFill,
  Html5Audio,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import Retailer from "./icons/Retailer";
import FirstStage from "./FirstStage";
import SecondStage from "./SecondStage";
import ThirdStage from "./ThirdStage";
import { useLanguage } from "../../locales/LanguageContext";

const SecondFrame = ({
  lang,
  audioList,
  audioFrameDurations,
  secondFrameDuration,
  targetLevel,
  bullishNumber,
  turnNeutral,
  icons,
  icon1,
  icon2,
  icon3,
  firstUnit,
  secondUnit,
  cnview,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { t } = useLanguage();

  const safeAudioList =
    Array.isArray(audioList) && audioList.length > 0 ? audioList : [];

  const frameDurations =
    audioFrameDurations && audioFrameDurations.length > 0
      ? audioFrameDurations
      : safeAudioList.length > 0
        ? safeAudioList.map(() =>
            Math.ceil(secondFrameDuration / safeAudioList.length),
          )
        : [secondFrameDuration];

  const playlistBoundaries = React.useMemo(() => {
    const boundaries = [0];
    for (let i = 0; i < frameDurations.length; i++) {
      boundaries.push(boundaries[i] + frameDurations[i]);
    }
    return boundaries;
  }, [frameDurations.join(",")]);

  const STAGE = {
    1:
      playlistBoundaries &&
      playlistBoundaries.length >= 3 &&
      playlistBoundaries[1] > 0
        ? playlistBoundaries[1]
        : Math.ceil(secondFrameDuration * 0.27),
    2:
      playlistBoundaries &&
      playlistBoundaries.length >= 3 &&
      playlistBoundaries[1] > 0
        ? playlistBoundaries[1]
        : Math.ceil(secondFrameDuration * 0.27),
    3:
      playlistBoundaries &&
      playlistBoundaries.length >= 3 &&
      playlistBoundaries[2] > 0
        ? playlistBoundaries[2]
        : Math.ceil(secondFrameDuration * 0.65),
  };

  const getCurrentStageMessage = () => {
    if (safeAudioList.length === 0) {
      return {
        stage: 1,
        message: t?.footer || "",
        startFrame: 0,
      };
    }
    for (let i = safeAudioList.length - 1; i >= 0; i--) {
      if (frame >= playlistBoundaries[i]) {
        return {
          stage: i + 1,
          message: safeAudioList[i]?.message || t?.footer || "",
          startFrame: playlistBoundaries[i],
        };
      }
    }
    return {
      stage: 1,
      message: safeAudioList[0]?.message || t?.footer || "",
      startFrame: playlistBoundaries[0] || 0,
    };
  };

  const currentStageInfo = getCurrentStageMessage();

  const displayUnderBanner = interpolate(frame, [0, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const ideaProgress = spring({
    fps,
    frame,
    config: { damping: 12 },
  });
  const ideaRotation = interpolate(ideaProgress, [0, 1], [-180, -270], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const ideaScale = interpolate(ideaProgress, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const translateY = interpolate(frame, [0, 40], [500, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        overflow: "hidden",
        opacity: interpolate(
          frame,
          [0, 40, secondFrameDuration, secondFrameDuration + 20],
          [0, 1, 1, 0],
          {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          },
        ),
      }}
    >
      {Array.isArray(audioList) &&
        audioList.map((audioItem, index) => {
          if (!audioItem || !audioItem.src) return null;
          const from = playlistBoundaries[index];
          const durationInFrames = playlistBoundaries[index + 1] - from;
          return (
            <Sequence
              key={`audio-${index}`}
              from={from}
              durationInFrames={durationInFrames}
            >
              <Html5Audio src={audioItem.src} playbackRate={1} volume={0.5} />
            </Sequence>
          );
        })}
      <div
        style={{
          position: "absolute",
          bottom: "64px",
          display: "flex",
          justifyContent: "left",
          width: "900px",
          left: "20px",
          zIndex: 40,
          transform: `translateY(${translateY}px)`,
        }}
      >
        <Retailer />
      </div>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "55%",
          transform: `translate(-50%, -50%) scale(${ideaScale}) rotate(${ideaRotation}deg)`,
        }}
      >
        <svg viewBox="0 0 100 100" width="1650" height="1650">
          <path
            d="M 20 40 A 30 40 0 0 1 80 40 L 50 90 Z"
            fill={lang === "tc-CN" ? "#0b3a66" : "#0979f1ff"}
          />
        </svg>
      </div>
      <FirstStage
        stageTiming={STAGE[1]}
        firstUnit={firstUnit}
        secondUnit={secondUnit}
      />
      <SecondStage
        stageTiming={STAGE[1]}
        stageEnding={STAGE[3]}
        icons={icons}
        icon1={icon1}
        icon2={icon2}
        icon3={icon3}
      />
      <ThirdStage
        stageTiming={STAGE[3]}
        targetLevel={targetLevel}
        bullishNumber={bullishNumber}
        turnNeutral={turnNeutral}
        firstUnit={firstUnit}
        secondUnit={secondUnit}
        viewLabel={cnview}
      />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: `rgba(255, 255, 255, 0)`,
          pointerEvents: "none",
          zIndex: 15,
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: 0,
          zIndex: 20,
          width: "100%",
          minHeight: "80px",
          backgroundColor: "#292929",
          color: "white",
          fontSize: "24px",
          fontWeight: "500",
          fontFamily:
            "'Noto Sans SC', 'Noto Sans TC', 'Microsoft YaHei', sans-serif",
          lineHeight: "1.3",
          padding: "10px",
          textAlign: "center",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          flexWrap: "wrap",
          opacity: displayUnderBanner,
          wordSpacing: "-2px",
          letterSpacing: "-0.5px",
        }}
      >
        {currentStageInfo.message &&
          currentStageInfo.message.split("\n").map((line, lineIndex) => (
            <div
              key={`line-${currentStageInfo.stage}-${lineIndex}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "1px",
                lineHeight: "1.3",
              }}
            >
              {line.split("").map((char, charIndex) => {
                const globalCharIndex =
                  currentStageInfo.message
                    .split("\n")
                    .slice(0, lineIndex)
                    .join("").length + charIndex;

                const charDelay = globalCharIndex * 0.4;
                const relativeFrame = frame - currentStageInfo.startFrame - 20;

                const charProgress = spring({
                  fps,
                  frame: relativeFrame - charDelay,
                  config: {
                    damping: 12,
                    stiffness: 100,
                  },
                });

                const shouldShow = relativeFrame >= charDelay;

                const charScale = interpolate(charProgress, [0, 1], [0.5, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });

                return (
                  <span
                    key={`${currentStageInfo.stage}-${lineIndex}-${charIndex}`}
                    style={{
                      display: "inline-block",
                      verticalAlign: "baseline",
                      transform: `scale(${charScale})`,
                      transformOrigin: "center baseline",
                      opacity: shouldShow ? charProgress : 0,
                      color: "white",
                      lineHeight: "1.3",
                      transition: "color 0.3s ease",
                    }}
                  >
                    {char === " " ? (
                      <span style={{ width: "2px", display: "inline-block" }} />
                    ) : (
                      char
                    )}
                  </span>
                );
              })}
            </div>
          ))}
      </div>
    </AbsoluteFill>
  );
};

export default SecondFrame;
